import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

/**
 * Given a default return destination, provide a function to navigate to the previous page.
 * If the current page is the first in the history stack, use the default destination from
 * `backToDestinationDefault`.
 * @param {Object} backToDestinationDefault - The default return destination.
 * @param {string} backToDestinationDefault.route - The route for the default return destination.
 * @param {string} backToDestinationDefault.text - The display name for the default return destination.
 * default behavior for `goBack` function.
 * @returns {{ backToDestination: { text: string, route: string }, goBack: () => void }} - The return destination and function for navigating to it
 */
export const useGoBack = (backToDestinationDefault) => {
    const location = useLocation();
    const navigate = useNavigate();

    // If current location state does not have a `backToDestination`, then use default.
    const backToDestination = location.state?.backToDestination ?? backToDestinationDefault;

    // If current location was navigated to internally, use delta of -1 to go to prevous location record.
    // Else, use default route.
    const goBack = useCallback(
        () => navigate(location.state?.backToDestination ? -1 : backToDestinationDefault.route),
        [backToDestinationDefault.route, location.state?.backToDestination, navigate],
    );

    return { backToDestination, goBack };
};
